import { useState } from "react";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const INITIAL = { full_name: "", company: "", email: "", phone: "", project_type: "", budget: "", timeline: "", description: "" };

function Field({ label, required, children, id }) {
  return (
    <label htmlFor={id} className="block">
      <span className="eyebrow mb-2 block">{label}{required && <span className="text-[var(--accent)]"> *</span>}</span>
      {children}
    </label>
  );
}

export default function InquiryForm() {
  const site = useSite();
  const types = site?.data?.project_types || [];
  const budgets = site?.data?.budgets || [];
  const cta = site?.data?.pages?.contact?.cta || "SUBMIT PROJECT INQUIRY";
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState({ state: "idle" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading" });
    try {
      const { data } = await api.post("/public/inquiries", form);
      setStatus({ state: "success", message: data.message });
      setForm(INITIAL);
    } catch (err) {
      setStatus({ state: "error", message: errorMessage(err) });
    }
  };

  if (status.state === "success") {
    return (
      <div className="surface rounded-lg p-10 text-center" data-testid="inquiry-success">
        <CheckCircle2 className="mx-auto h-10 w-10 text-[var(--accent)]" />
        <h3 className="mt-6 font-display text-2xl font-bold uppercase text-white">Inquiry received</h3>
        <p className="mt-3 text-[var(--text-dim)]">{status.message}</p>
        <button className="btn-ghost mt-8" onClick={() => setStatus({ state: "idle" })} data-testid="inquiry-another">Submit another inquiry</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="surface rounded-lg p-6 sm:p-10" data-testid="inquiry-form" noValidate>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Full Name" required id="inq-name"><input id="inq-name" className="field" value={form.full_name} onChange={set("full_name")} placeholder="Your name" required data-testid="inquiry-full-name" /></Field>
        <Field label="Company / Organization" id="inq-company"><input id="inq-company" className="field" value={form.company} onChange={set("company")} placeholder="Company name" data-testid="inquiry-company" /></Field>
        <Field label="Email Address" required id="inq-email"><input id="inq-email" type="email" className="field" value={form.email} onChange={set("email")} placeholder="you@company.com" required data-testid="inquiry-email" /></Field>
        <Field label="Phone / WhatsApp" id="inq-phone"><input id="inq-phone" className="field" value={form.phone} onChange={set("phone")} placeholder="+1 ..." data-testid="inquiry-phone" /></Field>
        <Field label="Project Type" required id="inq-type">
          <select id="inq-type" className="field" value={form.project_type} onChange={set("project_type")} required data-testid="inquiry-project-type">
            <option value="">Select a project type</option>{types.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Estimated Budget" required id="inq-budget">
          <select id="inq-budget" className="field" value={form.budget} onChange={set("budget")} required data-testid="inquiry-budget">
            <option value="">Select a budget range</option>{budgets.map((b) => <option key={b}>{b}</option>)}
          </select>
        </Field>
        <div className="sm:col-span-2"><Field label="Project Timeline" id="inq-timeline"><input id="inq-timeline" className="field" value={form.timeline} onChange={set("timeline")} placeholder="e.g. Launch within 3 months" data-testid="inquiry-timeline" /></Field></div>
        <div className="sm:col-span-2"><Field label="Project Description" required id="inq-desc"><textarea id="inq-desc" rows={6} className="field resize-y" value={form.description} onChange={set("description")} placeholder="Tell us about the product, goals, users and any technical requirements." required data-testid="inquiry-description" /></Field></div>
      </div>
      {status.state === "error" && <p className="mt-6 rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="inquiry-error">{status.message}</p>}
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-[var(--text-mute)]">We respond to every inquiry personally. Professional projects start from $1,000+.</p>
        <button type="submit" disabled={status.state === "loading"} className="btn-primary justify-center font-display text-[13px] tracking-[0.08em] disabled:opacity-60" data-testid="inquiry-submit">
          {status.state === "loading" ? "Submitting…" : cta} <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
}
